import React, { useEffect } from 'react'
import { useParams } from 'react-router-dom'
import {
  ChartOptionElement,
  useGetChartOptionQuery,
  useGetReportDetailsQuery,
  useGetReportFavoriteDetailsQuery,
} from 'gqlTypes'
import pick from 'lodash/pick'
import first from 'lodash/first'
import Box from '@mui/material/Box'
import LinearProgress from '@mui/material/LinearProgress'
import Navbar from './Navbar'
import ReportHeader from './ReportHeader'
import ReportContents from './ReportContents'
import ReportElements from './ReportElements'
import { ChartOptionsContextProvider } from 'contexts/ChartOptionsContext'
import { useSetRecoilState } from 'recoil'
import { chartOptionAtom, keyForElementIdAndType } from 'recoil/settings'
import ReportOverview from './ReportOverview/ReportOverview'
import ReportNavigation from './ReportNavigation'

const Report: React.FC = () => {
  const { id, sectionId } = useParams()
  const setGlobalOptions = useSetRecoilState(
    chartOptionAtom(
      keyForElementIdAndType({ id: id || '', type: ChartOptionElement.Report })
    )
  )

  const { data: chartOptionData } = useGetChartOptionQuery({
    variables: { id: id || '', type: ChartOptionElement.Report },
  })

  const { data, refetch, loading } = useGetReportDetailsQuery({
    variables: { id: id ?? '' },
  })

  const {
    data: favoriteData,
    refetch: favoriteRefetch,
    loading: favoriteLoading,
  } = useGetReportFavoriteDetailsQuery({
    variables: { id: id ?? '' },
  })

  const handleUpdateFavorites = () => {
    refetch()
    favoriteRefetch()
  }

  useEffect(() => {
    if (chartOptionData) {
      if (chartOptionData.chartOption) {
        setGlobalOptions(
          pick(chartOptionData.chartOption, [
            'cohort',
            'subgroup',
            'pastResults',
            'config',
          ])
        )
      } else {
        setGlobalOptions({
          cohort: first(data?.report?.cohorts),
          subgroup: first(data?.report?.subgroups),
          pastResults: true,
        })
      }
    }
  }, [chartOptionData, data])

  if (loading) {
    return (
      <>
        <Navbar />
        <LinearProgress />
      </>
    )
  }

  const report = data?.report

  if (!report) {
    return <h1>Report not found</h1>
  }

  return (
    <>
      <Navbar />
      <ReportHeader report={report} sectionId={sectionId} />
      <Box sx={{ display: 'flex', maxWidth: '100%' }}>
        <ReportContents report={report} sectionId={sectionId} />
        {sectionId ? (
          <Box
            sx={{
              p: 4,
              width: 'calc(100% - 300px)',
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
            }}
          >
            <ReportNavigation report={report} sectionId={sectionId} />
            <ReportElements
              report={report}
              sectionId={sectionId}
              onUpdateFavorites={handleUpdateFavorites}
            />
            <ReportNavigation report={report} sectionId={sectionId} />
          </Box>
        ) : (
          <ReportOverview report={report} />
        )}
      </Box>
    </>
  )
}

const WrappedReport: React.FC = (props) => {
  const { id } = useParams()
  return (
    <ChartOptionsContextProvider id={id || ''} type={ChartOptionElement.Report}>
      <Report {...props} />
    </ChartOptionsContextProvider>
  )
}

export default WrappedReport
